import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { SectionCard, Divider } from "@/components/ds";
import { Lightbulb } from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";

// Salesforce brand blue
const SF_BLUE = "#00a1e0";

interface SalesforceConfig {
  id: number;
  siteId: number;
  displayName: string;
  instanceUrl: string;
  clientIdMasked: string;
  refreshTokenMasked: string;
  orgId: string;
  orgName: string;
  enabled: boolean;
  createdAt: string;
}

interface ConfigForm {
  displayName: string;
  instanceUrl: string;
  clientId: string;
  clientSecret: string;
  refreshToken: string;
}

const EMPTY_FORM: ConfigForm = {
  displayName: "Salesforce",
  instanceUrl: "",
  clientId: "",
  clientSecret: "",
  refreshToken: ""
};

const CAPABILITIES = [
  {
    title: "Contact & Account Management",
    icon: "User",
    items: [
      "Search any Salesforce contact by name, email, or phone — returns full profile, account association, and open cases",
      "Retrieve full contact details including custom fields, associated accounts, and activity history",
      "Create new Contact records directly from chat — name, email, phone, and account automatically populated",
      "Update contact fields (title, phone, owner, custom fields) from conversation data without leaving the chat"
    ]
  },
  {
    title: "Lead & Opportunity Management",
    icon: "Briefcase",
    items: [
      "Create new Lead records when a visitor isn't already in Salesforce — captured from natural conversation",
      "Search existing leads by name or email to prevent duplicates before creating new records",
      "Create and update Opportunities — stage, amount, close date — directly from the conversation",
      "Get all open opportunities for a contact to give agents full pipeline context before they respond"
    ]
  },
  {
    title: "Case Management",
    icon: "Tag",
    items: [
      "Open new support cases directly from chat — subject, description, priority, and contact auto-linked",
      "Search and retrieve existing cases for a contact — status, priority, and case number at a glance",
      "Update case status and priority from chat without requiring an agent to log into Salesforce",
      "Add public case comments — keeps the case trail complete with chat context"
    ]
  },
  {
    title: "Task & Note Logging",
    icon: "Edit",
    items: [
      "Create follow-up tasks assigned to any Salesforce owner with due date — no manual entry required",
      "Add notes to any Salesforce record (Contact, Lead, Opportunity, Account) from the conversation",
      "Get open tasks for a contact so agents see pending follow-ups before they respond"
    ]
  }
];

const AI_TOOLS = [
  // Contacts (5)
  { name: "salesforce_search_contact", label: "Search contact", group: "Contacts", description: "Search by name, email, or phone" },
  { name: "salesforce_get_contact", label: "Get contact details", group: "Contacts", description: "Full contact profile by Salesforce ID" },
  { name: "salesforce_create_contact", label: "Create contact", group: "Contacts", description: "Create a new Contact record" },
  { name: "salesforce_update_contact", label: "Update contact", group: "Contacts", description: "Update contact fields" },
  { name: "salesforce_get_contact_cases", label: "Get contact cases", group: "Contacts", description: "All cases for a contact" },
  // Accounts (3)
  { name: "salesforce_search_accounts", label: "Search accounts", group: "Accounts", description: "Search accounts by name" },
  { name: "salesforce_get_account", label: "Get account", group: "Accounts", description: "Full account details" },
  { name: "salesforce_get_account_contacts", label: "Account contacts", group: "Accounts", description: "List contacts at an account" },
  // Leads (4)
  { name: "salesforce_create_lead", label: "Create lead", group: "Leads", description: "Create a new Lead record" },
  { name: "salesforce_get_lead", label: "Get lead", group: "Leads", description: "Lead details by ID" },
  { name: "salesforce_search_leads", label: "Search leads", group: "Leads", description: "Search leads by name or email" },
  { name: "salesforce_update_lead", label: "Update lead", group: "Leads", description: "Update lead fields" },
  // Opportunities (5)
  { name: "salesforce_create_opportunity", label: "Create opportunity", group: "Opportunities", description: "Create a new Opportunity" },
  { name: "salesforce_get_opportunity", label: "Get opportunity", group: "Opportunities", description: "Opportunity details by ID" },
  { name: "salesforce_get_contact_opportunities", label: "Contact opportunities", group: "Opportunities", description: "All opportunities for a contact" },
  { name: "salesforce_search_opportunities", label: "Search opportunities", group: "Opportunities", description: "Search by name or stage" },
  { name: "salesforce_update_opportunity", label: "Update opportunity", group: "Opportunities", description: "Update stage, amount, close date" },
  // Cases (5)
  { name: "salesforce_create_case", label: "Create case", group: "Cases", description: "Open a new support case" },
  { name: "salesforce_get_case", label: "Get case", group: "Cases", description: "Case details by ID" },
  { name: "salesforce_search_cases", label: "Search cases", group: "Cases", description: "Search cases by subject" },
  { name: "salesforce_update_case", label: "Update case", group: "Cases", description: "Update status, priority" },
  { name: "salesforce_add_case_comment", label: "Add case comment", group: "Cases", description: "Add a public comment to a case" },
  // Tasks & Notes (3)
  { name: "salesforce_create_task", label: "Create task", group: "Tasks & Notes", description: "Create a follow-up task" },
  { name: "salesforce_get_tasks", label: "Get tasks", group: "Tasks & Notes", description: "Open tasks for a contact or record" },
  { name: "salesforce_add_note", label: "Add note", group: "Tasks & Notes", description: "Add a note to any Salesforce record" }
];

const AI_TOOL_GROUPS = ["Contacts", "Accounts", "Leads", "Opportunities", "Cases", "Tasks & Notes"];

// ── Salesforce logo ────────────────────────────────────────────────────────────

function SalesforceLogo({ size = 32 }: { size?: number }) {
  return <img src="/img/salesforce-crm.svg" alt="Salesforce" width={size} height={size} />;
}

// ── Configuration tab ──────────────────────────────────────────────────────────

interface ConfigTabProps {
  config: SalesforceConfig | null;
  onSaved(config: SalesforceConfig): void;
  onDeleted(): void;
}

function ConfigTab({ config, onSaved, onDeleted }: ConfigTabProps) {
  const api = useApi();
  const [form, setForm] = useState<ConfigForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (config) {
      setForm({
        displayName: config.displayName ?? "Salesforce",
        instanceUrl: config.instanceUrl ?? "",
        clientId: "",   // never pre-fill secrets from masked values
        clientSecret: "",
        refreshToken: ""
      });
    }
  }, [config]);

  function update(field: keyof ConfigForm, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError(null);
    setSuccess(false);
    setTestResult(null);
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const resp = await api.admin.post("Salesforce/Test", {
        instanceUrl: form.instanceUrl,
        clientId: form.clientId,
        clientSecret: form.clientSecret,
        refreshToken: form.refreshToken
      });
      const data = await resp.json();
      if (data?.valid) {
        setTestResult({ valid: true, message: `Connected to org: ${data.orgName || data.orgId || "Salesforce"}` });
      } else {
        setTestResult({ valid: false, message: data?.error ?? "Connection test failed." });
      }
    } catch {
      setTestResult({ valid: false, message: "Connection test failed. Check your credentials." });
    } finally {
      setTesting(false);
    }
  }

  async function handleSave() {
    if (!form.instanceUrl.trim()) { setError("Instance URL is required."); return; }
    if (!form.clientId.trim()) { setError("Client ID is required."); return; }
    if (!form.clientSecret.trim()) { setError("Client Secret is required."); return; }
    if (!form.refreshToken.trim()) { setError("Refresh Token is required."); return; }

    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const resp = await api.admin.post("Salesforce/Config", form);
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setError(data?.error ?? "Failed to save configuration.");
        return;
      }
      const saved = await resp.json();
      setSuccess(true);
      onSaved(saved);
      // Clear secrets from form after save
      setForm((prev) => ({ ...prev, clientId: "", clientSecret: "", refreshToken: "" }));
    } catch {
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      const resp = await api.admin.delete("Salesforce/Config");
      if (!resp.ok) {
        setError("Failed to disconnect. Please try again.");
        return;
      }
      setShowDeleteConfirm(false);
      setForm(EMPTY_FORM);
      onDeleted();
    } catch {
      setError("Failed to disconnect. Please try again.");
    } finally {
      setDeleting(false);
    }
  }

  const canTest = form.instanceUrl.trim() && form.clientId.trim() && form.clientSecret.trim() && form.refreshToken.trim();

  return (
    <div className="space-y-4">
      {/* Current connection status */}
      {config && (
        <SectionCard title="Current Connection" action={<span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">Connected</span>}>
          <>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-xs text-zinc-400 mb-0.5">Instance URL</div>
              <div className="text-zinc-700 font-medium truncate">{config.instanceUrl}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-0.5">Org</div>
              <div className="text-zinc-700 font-medium truncate">{config.orgName || config.orgId || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-0.5">Client ID</div>
              <div className="text-zinc-500 font-mono text-xs">{config.clientIdMasked}</div>
            </div>
            <div>
              <div className="text-xs text-zinc-400 mb-0.5">Refresh Token</div>
              <div className="text-zinc-500 font-mono text-xs">{config.refreshTokenMasked}</div>
            </div>
          </div>

          {showDeleteConfirm ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="text-sm text-red-700 mb-3">Disconnect Salesforce? This will disable all AI tools and workflows that rely on this connection.</div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleting}
                  className="text-sm px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? "Disconnecting…" : "Yes, disconnect"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-sm px-3 py-1.5 border border-zinc-200 text-zinc-600 rounded-lg hover:bg-zinc-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="mt-4 text-sm text-red-500 hover:text-red-700"
            >
              Disconnect Salesforce
            </button>
          )}
          </>
        </SectionCard>
      )}

      {/* Credentials form */}
      <SectionCard
        title={config ? "Update Credentials" : "Connect Salesforce"}
        description={config
          ? "Enter new credentials to update your connection. Leave credential fields blank to keep existing values."
          : "Paste your Salesforce Connected App credentials below."}
      >

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={form.displayName}
              onChange={(e) => update("displayName", e.target.value)}
              placeholder="e.g. Production Salesforce"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ ["--tw-ring-color" as string]: SF_BLUE }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Instance URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={form.instanceUrl}
              onChange={(e) => update("instanceUrl", e.target.value)}
              placeholder="https://mycompany.my.salesforce.com"
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            />
            <div className="text-xs text-zinc-400 mt-1">Your Salesforce org URL — no trailing slash.</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Client ID (Consumer Key) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.clientId}
              onChange={(e) => update("clientId", e.target.value)}
              placeholder={config ? "Enter new Client ID to replace" : "3MVG9..."}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Client Secret (Consumer Secret) <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={form.clientSecret}
              onChange={(e) => update("clientSecret", e.target.value)}
              placeholder={config ? "Enter new Client Secret to replace" : "••••••••"}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-700 mb-1">
              Refresh Token <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={form.refreshToken}
              onChange={(e) => update("refreshToken", e.target.value)}
              placeholder={config ? "Enter new Refresh Token to replace" : "5Aep8..."}
              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:border-transparent"
            />
            <div className="text-xs text-zinc-400 mt-1">
              Run <code className="bg-zinc-100 px-1 rounded">sf org display --target-org myOrg --verbose</code> in Salesforce CLI to get your refresh token.
            </div>
          </div>
        </div>

        {/* Test result */}
        {testResult && (
          <div className={`mt-4 p-3 rounded-lg text-sm ${testResult.valid ? "bg-green-50 border border-green-200 text-green-700" : "bg-red-50 border border-red-200 text-red-700"}`}>
            {testResult.valid ? "✓ " : "✗ "}{testResult.message}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
        )}

        {success && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
            ✓ Salesforce configuration saved successfully.
          </div>
        )}

        <div className="flex gap-3 mt-5">
          <button
            type="button"
            onClick={handleTest}
            disabled={!canTest || testing}
            className="text-sm px-4 py-2 border border-zinc-200 text-zinc-600 rounded-lg hover:bg-zinc-50 disabled:opacity-40"
          >
            {testing ? "Testing…" : "Test Connection"}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 disabled:opacity-50 font-medium"
            style={{ backgroundColor: SF_BLUE }}
          >
            {saving ? "Saving…" : config ? "Update" : "Save & Connect"}
          </button>
        </div>
      </SectionCard>

      {/* Setup instructions */}
      <SectionCard title="How to get your credentials">
        <ol className="space-y-3 text-sm text-zinc-600">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold mt-0.5" style={{ backgroundColor: SF_BLUE }}>1</span>
            <div>
              In Salesforce, go to <strong>Setup → Apps → App Manager → New Connected App</strong>.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold mt-0.5" style={{ backgroundColor: SF_BLUE }}>2</span>
            <div>
              Enable <strong>OAuth Settings</strong>. Set Callback URL to <code className="bg-zinc-100 px-1 rounded">https://login.salesforce.com/services/oauth2/success</code>.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold mt-0.5" style={{ backgroundColor: SF_BLUE }}>3</span>
            <div>
              Add OAuth scopes: <strong>api</strong> and <strong>refresh_token, offline_access</strong>. Save the app (wait 2-10 minutes for activation).
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold mt-0.5" style={{ backgroundColor: SF_BLUE }}>4</span>
            <div>
              Copy the <strong>Consumer Key</strong> (Client ID) and <strong>Consumer Secret</strong> from the Connected App details.
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-xs flex items-center justify-center font-semibold mt-0.5" style={{ backgroundColor: SF_BLUE }}>5</span>
            <div>
              To get a Refresh Token, install the Salesforce CLI and run:{" "}
              <code className="bg-zinc-100 px-1 rounded text-xs">sf org login web --alias myOrg</code>, then{" "}
              <code className="bg-zinc-100 px-1 rounded text-xs">sf org display --target-org myOrg --verbose</code>. Copy the <strong>Refresh Token</strong>.
            </div>
          </li>
        </ol>
      </SectionCard>
    </div>
  );
}

// ── AI Tools tab ───────────────────────────────────────────────────────────────

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  return (
    <SectionCard
      title="AI Tools"
      description="25 tools available to any AI workflow node on this site. No per-node configuration required — the AI decides when to call Salesforce based on conversation context."
      action={hasConnection ? <LaunchWizardButton onClick={onOpenWizard} /> : undefined}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasConnection ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}>
          {hasConnection ? "Active" : "No active connection"}
        </span>
      </div>

      <Divider />
      <div className="pt-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">Available AI tools</div>
          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">25 tools</span>
        </div>

        {AI_TOOL_GROUPS.map((group) => (
          <div key={group} className="mb-4">
            <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div
                  key={tool.name}
                  className={`flex gap-3 p-3 rounded-lg border ${hasConnection ? "border-green-200 bg-green-50" : "border-zinc-200 bg-zinc-50"}`}
                >
                  <div className={`mt-0.5 w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center ${hasConnection ? "bg-green-500" : "bg-zinc-300"}`}>
                    {hasConnection && (
                      <svg viewBox="0 0 10 10" width="8" height="8" fill="none">
                        <path d="M1.5 5L3.5 7L8.5 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-zinc-700">{tool.label}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{tool.description}</div>
                    <code className="text-[10px] text-zinc-400 mt-1 block">{tool.name}</code>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8">
        <Divider />
        <div className="pt-8">
          <SkillControlsPanel integration="salesforce" skills={AI_TOOLS} />
        </div>
      </div>
    </SectionCard>
  );
}

// ── Overview tab ───────────────────────────────────────────────────────────────

function OverviewTab({ hasConnection, onConnect, onOpenWizard }: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div className="space-y-4">
      {/* Hero card */}
      <SectionCard title="Salesforce CRM" action={hasConnection ? (
          <span className="text-xs bg-green-100 text-green-700 font-medium px-2.5 py-1 rounded-full">Connected</span>
        ) : (
          <button type="button" onClick={onConnect} className="text-sm px-4 py-2 text-white rounded-lg hover:opacity-90 font-medium" style={{ backgroundColor: SF_BLUE }}>Connect</button>
        )}>
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${SF_BLUE}18` }}>
            <SalesforceLogo size={32} />
          </div>
          <div className="flex-1">
            <div className="text-zinc-500 text-sm leading-relaxed mb-4">
              Connect Salesforce to give your AI workflows full CRM context: look up contacts, manage leads, create cases, update opportunities, and log notes — all from the chat window, in real time.
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-sky-100 text-sky-700">25 AI tools</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">5 record types</span>
              <span className="text-xs font-semibold px-2 py-1 rounded-full bg-zinc-100 text-zinc-600">Starter Wizard included</span>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Capability cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CAPABILITIES.map((cap, i) => (
          <SectionCard key={i} title={cap.title}>
            <div className="flex items-center gap-2 mb-3 -mt-1">
              <span className="text-zinc-600"><IconByName name={cap.icon} className="w-5 h-5" /></span>
            </div>
            <div className="space-y-1.5">
              {cap.items.slice(0, 3).map((item, j) => (
                <div key={j} className="flex items-start gap-2">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: SF_BLUE }} />
                  <span className="text-xs text-zinc-600">{item}</span>
                </div>
              ))}
            </div>
          </SectionCard>
        ))}
      </div>

      {/* How it works */}
      <SectionCard title="How it works">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { step: "1", title: "Connect your org", body: "Paste your Salesforce Connected App credentials. Velaro uses the OAuth refresh token flow — no re-authorization required." },
            { step: "2", title: "Add an AI workflow node", body: "Create a workflow with an AI node. The 25 Salesforce tools are automatically available — no per-node setup needed." },
            { step: "3", title: "AI resolves queries in real time", body: "When a visitor asks about their account, order, or case, the AI calls Salesforce directly and responds with live data." }
          ].map((s) => (
            <div key={s.step} className="flex gap-3">
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full text-white text-xs flex items-center justify-center font-bold mt-0.5"
                style={{ backgroundColor: SF_BLUE }}
              >
                {s.step}
              </span>
              <div>
                <div className="text-sm font-semibold text-zinc-700 mb-1">{s.title}</div>
                <div className="text-xs text-zinc-500 leading-relaxed">{s.body}</div>
              </div>
            </div>
          ))}
        </div>

        {hasConnection && (
          <>
          <Divider />
          <div className="mt-5 pt-4 flex gap-3">
            <button
              type="button"
              onClick={onOpenWizard}
              className="text-sm px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-medium shadow-sm"
            >
              Launch Starter Wizard
            </button>
            <span className="text-xs text-zinc-400 self-center">Create a working Salesforce workflow in 60 seconds</span>
          </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}

// ── Field Reference ─────────────────────────────────────────────────────────────

const SF_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "Id", label: "Contact ID", notes: "Salesforce record ID" },
      { field: "FirstName", label: "First Name", notes: "" },
      { field: "LastName", label: "Last Name", notes: "" },
      { field: "Email", label: "Email", notes: "Primary identifier" },
      { field: "Phone", label: "Phone", notes: "" },
      { field: "MobilePhone", label: "Mobile Phone", notes: "" },
      { field: "Title", label: "Job Title", notes: "" },
      { field: "Department", label: "Department", notes: "" },
      { field: "AccountId", label: "Account ID", notes: "Linked account record" },
      { field: "OwnerId", label: "Owner ID", notes: "Assigned Salesforce user" },
      { field: "CreatedDate", label: "Created Date", notes: "ISO datetime" }
    ]
  },
  {
    scope: "Account",
    scopeKey: "account",
    fields: [
      { field: "Id", label: "Account ID", notes: "" },
      { field: "Name", label: "Account Name", notes: "" },
      { field: "Website", label: "Website", notes: "" },
      { field: "Phone", label: "Phone", notes: "" },
      { field: "Industry", label: "Industry", notes: "" },
      { field: "AnnualRevenue", label: "Annual Revenue", notes: "Numeric" },
      { field: "NumberOfEmployees", label: "Employees", notes: "Numeric" },
      { field: "BillingCity", label: "City", notes: "" },
      { field: "BillingCountry", label: "Country", notes: "" },
      { field: "OwnerId", label: "Owner ID", notes: "" }
    ]
  },
  {
    scope: "Lead",
    scopeKey: "lead",
    fields: [
      { field: "Id", label: "Lead ID", notes: "" },
      { field: "FirstName", label: "First Name", notes: "" },
      { field: "LastName", label: "Last Name", notes: "" },
      { field: "Email", label: "Email", notes: "" },
      { field: "Phone", label: "Phone", notes: "" },
      { field: "Company", label: "Company", notes: "" },
      { field: "Title", label: "Job Title", notes: "" },
      { field: "Status", label: "Status", notes: "Open - Not Contacted / Working - Contacted / Closed - Converted / Closed - Not Converted" },
      { field: "LeadSource", label: "Lead Source", notes: "Web / Phone Inquiry / Partner Referral / Chat / etc." }
    ]
  },
  {
    scope: "Opportunity",
    scopeKey: "opportunity",
    fields: [
      { field: "Id", label: "Opportunity ID", notes: "" },
      { field: "Name", label: "Opportunity Name", notes: "" },
      { field: "StageName", label: "Stage", notes: "Prospecting / Qualification / Proposal / Closed Won / Closed Lost / etc." },
      { field: "Amount", label: "Amount", notes: "Numeric, no currency symbol" },
      { field: "CloseDate", label: "Close Date", notes: "ISO date" },
      { field: "Probability", label: "Win Probability", notes: "0–100" },
      { field: "AccountId", label: "Account ID", notes: "" },
      { field: "OwnerId", label: "Owner ID", notes: "" }
    ]
  },
  {
    scope: "Case",
    scopeKey: "case",
    fields: [
      { field: "Id", label: "Case ID", notes: "" },
      { field: "CaseNumber", label: "Case Number", notes: "Auto-generated (e.g. 00001234)" },
      { field: "Subject", label: "Subject", notes: "" },
      { field: "Status", label: "Status", notes: "New / Working / Escalated / Closed" },
      { field: "Priority", label: "Priority", notes: "Low / Medium / High" },
      { field: "Origin", label: "Origin", notes: "Web / Chat / Email / Phone" },
      { field: "AccountId", label: "Account ID", notes: "" },
      { field: "ContactId", label: "Contact ID", notes: "" },
      { field: "CreatedDate", label: "Created Date", notes: "ISO datetime" }
    ]
  }
];

function SfCopyVariable({ scopeKey, field }: { scopeKey: string; field: string }) {
  const [copied, setCopied] = React.useState(false);
  const variable = `{salesforce.${scopeKey}.${field}}`;
  async function copy() {
    try { await navigator.clipboard.writeText(variable); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* */ }
  }
  return (
    <button type="button" onClick={copy} title={`Copy: ${variable}`} aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied ? "border-green-300 bg-green-50 text-green-700" : "border-zinc-200 bg-zinc-50 text-zinc-400 hover:border-sky-300 hover:text-sky-600 hover:bg-sky-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function SalesforceFieldReference() {
  const [activeScope, setActiveScope] = React.useState("Contact");
  const scopeData = SF_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? SF_FIELD_REFERENCE[0];
  return (
    <SectionCard title="Field Reference" description="Standard Salesforce object field names and the variable syntax to use in workflow HTTP POST nodes. The agent CRM panel shows contact, account, opportunity, and case data automatically.">
      <>
      <div className="rounded-lg p-4 mb-5 text-xs" style={{ backgroundColor: "#e8f5fc", border: "1px solid #b3dff5" }}>
        <div className="grid grid-cols-1 gap-2">
          <div className="flex items-start gap-2" style={{ color: "#005fb2" }}>
            <span style={{ color: "#005fb2" }}><IconByName name="CheckCircle" className="w-4 h-4" /></span>
            <span><strong>Agent sidebar</strong> — automatically shows the matched contact, linked account, open opportunities, and cases during every live chat.</span>
          </div>
          <div className="flex items-start gap-2" style={{ color: "#005fb2" }}>
            <span style={{ color: "#005fb2" }}><IconByName name="FileText" className="w-4 h-4" /></span>
            <span><strong>HTTP POST variables</strong> — copy any variable below and paste into a workflow HTTP POST node to forward Salesforce data to another system.</span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {SF_FIELD_REFERENCE.map((s) => (
          <button key={s.scope} type="button" onClick={() => setActiveScope(s.scope)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              activeScope === s.scope ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"
            }`}
            style={activeScope === s.scope ? { backgroundColor: SF_BLUE } : {}}
          >{s.scope}</button>
        ))}
      </div>
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 mb-4 text-xs">
        <div className="flex items-center justify-between mb-1.5">
          <strong className="text-zinc-700">Using these in a workflow HTTP POST node</strong>
          <span className="text-zinc-400">Click any variable to copy</span>
        </div>
        <pre className="bg-zinc-900 text-zinc-100 rounded-lg p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">{`{
  "contact_email":  "{salesforce.contact.Email}",
  "contact_name":   "{salesforce.contact.FirstName} {salesforce.contact.LastName}",
  "account_name":   "{salesforce.account.Name}",
  "opportunity":    "{salesforce.opportunity.Name}",
  "deal_stage":     "{salesforce.opportunity.StageName}",
  "case_number":    "{salesforce.case.CaseNumber}"
}`}</pre>
      </div>
      <div className="rounded-xl border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 text-white text-xs font-semibold" style={{ backgroundColor: "#1a1a2e" }}>
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Salesforce field (API name)</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Friendly name</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Variable to copy</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-zinc-50/50"}>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">{f.field}</td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2"><SfCopyVariable scopeKey={scopeData.scopeKey} field={f.field} /></td>
                  <td className="px-3 py-2 text-zinc-400 italic text-[10px]">{f.notes || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs text-zinc-400 mt-3">
        Salesforce API names are case-sensitive (PascalCase). Custom fields end in <code className="bg-zinc-100 px-1 rounded">__c</code> — use the same variable syntax with your custom field API name.
      </p>
      </>
    </SectionCard>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

type TabId = "overview" | "configuration" | "ai-tools" | "field-reference" | "logs";

export default function Salesforce() {
  const api = useApi();
  const [config, setConfig] = useState<SalesforceConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await api.admin.get("Salesforce/Config");
      const data = await resp.json();
      setConfig(Array.isArray(data) && data.length > 0 ? data[0] : null);
    } catch {
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const TABS: { id: TabId; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "configuration", label: "Configuration" },
    { id: "ai-tools", label: "AI Tools" },
    { id: "field-reference", label: "Field Reference" },
    { id: "logs", label: "Activity Log" }
  ];

  return (
    <div>
      {showWizard && (
        <StarterWorkflowWizard
          integration="salesforce"
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Tabs */}
      <div className="flex border-b border-zinc-200 mb-4">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`py-2 mr-4 text-sm cursor-pointer ${activeTab === tab.id ? "border-b-2 border-primary text-primary font-medium" : "text-zinc-500"}`}
            style={activeTab === tab.id ? { borderColor: SF_BLUE, color: SF_BLUE } : {}}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {!!config?.enabled && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-blue-800 mb-0.5">
                Deploy the chat widget to your site
              </div>
              <div className="text-xs text-blue-600">
                Your Salesforce integration is connected — grab your embed code from Deployments to go live.
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window" className="text-xs px-3 py-1.5 rounded-lg border border-blue-300 bg-blue-50/60 text-blue-700 hover:bg-blue-100 transition">Chat Window</Link>
              <Link to="/deployment" className="text-xs px-3 py-1.5 rounded-lg bg-[var(--color-primary)] hover:opacity-90 text-white transition">Get Embed Code →</Link>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
            <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              <strong>Tip:</strong> You can also create a <strong>Knowledge Base Q&A</strong> page widget
              from <Link to="/deployment" className="underline hover:text-blue-900">Deployments → Page Widgets</Link> for
              a lightweight, AI-only Q&A experience — no live agent needed.
            </span>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={!!config?.enabled}
              onConnect={() => setActiveTab("configuration")}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}

          {activeTab === "configuration" && (
            <ConfigTab
              config={config}
              onSaved={(saved) => { setConfig(saved); }}
              onDeleted={() => setConfig(null)}
            />
          )}

          {activeTab === "ai-tools" && (
            <AiToolsTab
              hasConnection={!!config?.enabled}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}

          {activeTab === "field-reference" && (
            <SalesforceFieldReference />
          )}

          {activeTab === "logs" && (
            <SectionCard title="Activity Log">
              <IntegrationActivityLog integrationSlug="Salesforce" />
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}

export function BackToIntegrationLandingSalesforce() {
  const navigate = useNavigate();
  return (
    <div className="flex cursor-pointer" onClick={() => navigate("/integrations")} role="button" aria-label="Back to integrations">
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
